import { DrcRate } from './DrcRate';
import { DrcAnswer } from './DrcAnswer';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChatMessage, ThreadItem } from '@/data/types';
import { isMessage } from '@/data/store';
import { ArrowDownIcon, MailIcon, PauseIcon, PlayIcon, ReplyIcon, StarIcon, Ticks } from './Icons';
import { Swipeable } from './Swipeable';
import { LeadCard } from './LeadCard';
import { AiLeo, AiTag } from '@/components/AiLeo';
import { TeamAvatar } from '@/components/Avatar';

// The chat thread: date marks, notices and message bubbles, with WhatsApp's gestures:
// swipe a message right to reply, long-press for its menu. The person's earlier chats
// are folded behind one line until asked for. Lead details live on the Info screen; a
// website form lead's card sits in the thread with its Call, WhatsApp and Email buttons.

const WAVE = Array.from({ length: 28 }, (_, i) => 20 + Math.round(Math.abs(Math.sin(i * 1.7)) * 75));

function VoiceNote({ duration }: { duration: string }) {
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => setPlaying(false), 3000);
    return () => window.clearTimeout(t);
  }, [playing]);
  return (
    <div className="chats-voice">
      <button
        type="button"
        className="chats-voice-play"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => setPlaying((p) => !p)}
        aria-label={playing ? 'Pause voice note' : `Play voice note, ${duration}`}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <div className={`chats-wave${playing ? ' chats-wave--playing' : ''}`}>
        {WAVE.map((h, i) => <i key={i} className={`chats-wave-bar chats-wave-bar--h${Math.round(h / 10)}`} />)}
      </div>
    </div>
  );
}

function CoachHead({ post }: { post: NonNullable<ChatMessage['coachPost']> }) {
  const [name, date] = post.title.split(' · ');
  return (
    <div className="chats-coach-head">
      <span className={`chats-coach-chip chats-coach-chip--${post.type}`}>{name}</span>
      {date && <span className="chats-coach-date">{date}</span>}
    </div>
  );
}

function Bubble({ message, onReactions }: { message: ChatMessage; onReactions?: () => void }) {
  const outgoing = message.from === 'out' || message.from === 'auto';
  const base = message.from === 'note' || message.teamNote ? 'chats-msg chats-msg--note' : outgoing ? `chats-msg chats-msg--out${message.assistant ? ' chats-msg--leo' : ''}` : 'chats-msg chats-msg--in';
  const cls = message.email ? `${base} chats-msg--email` : base;
  const label = message.from === 'auto'
    ? 'Auto reply'
    : message.from === 'note'
      ? (message.author ? `Team note from ${message.author}, customer can't see this` : "Team note, customer can't see this")
      : message.approved
        ? 'Approved message'
        : message.author;
  const { body } = message;
  // Groups and channels show every reaction with its count (22 Oct 2026); one-to-one chats one each, as WhatsApp.
  const all = message.reactionsAll;
  const reactions = all ? all.map((r) => r.emoji) : [message.theirReaction, message.reaction].filter(Boolean);
  // Leo's name in violet; the team's (auto reply, a person) in green, as the agreed mock-up.
  const labelClass = `chats-msg-label${outgoing && !message.assistant ? ' chats-msg-label--team' : ''}`;

  return (
    <div className={`chats-msg-wrap${outgoing || message.from === 'note' ? ' chats-msg-wrap--out' : ''}${reactions.length ? ' chats-msg-wrap--reacted' : ''}`}>
      <div className={cls}>
        {/* An email reads as an email (Gary, 19 Oct): envelope, "Email" and the subject on top. */}
        {message.email && (
          <div className="chats-msg-email"><MailIcon /><b>Email</b>{message.email.subject && <span>{message.email.subject}</span>}</div>
        )}
        {message.forwarded && <div className="chats-msg-forwarded">Forwarded</div>}
        {message.teamNote && <div className="chats-msg-forwarded">Note · head office and Ted only</div>}
        {label && <div className={labelClass}>{message.aiMember && <TeamAvatar who={message.aiMember} tiny />}{message.assistant ? <AiLeo /> : label}</div>}
        {/* Ted's coaching posts: what kind of post it is, at a glance (Gary, 2 Nov 2026). */}
        {message.coachPost && <CoachHead post={message.coachPost} />}
        {message.replyTo && (
          <div className="chats-msg-quote">
            <b>{message.replyTo.author}</b>
            <span>{message.replyTo.preview}</span>
          </div>
        )}
        {/* AI team posts read as formatted answers (Gary, 1 Nov: easy to read), as in Ask. */}
        {/* Ted's coaching posts: section headings stand out (Gary, 2 Nov 2026); other AI posts unchanged. */}
        {body.kind === 'text' && message.aiTeam && (message.coachPost ? <div className="chats-coach-body"><DrcAnswer text={body.text} /></div> : <DrcAnswer text={body.text} />)}
        {body.kind === 'text' && !message.aiTeam && <span className={`chats-msg-text${message.deleted ? ' chats-msg-text--deleted' : ''}`}>{body.text}</span>}
        {body.kind === 'photo' && (body.url
          // Tap to see it full size, as WhatsApp (Gary, 16 Oct). A long press still opens the menu.
          ? <><a className="chats-photo-link" href={body.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><img className="chats-photo-img" src={body.url} alt={body.caption || 'Photo from the customer'} loading="lazy" /></a>{body.caption}</>
          : <div className="chats-photo">{body.caption}</div>)}
        {body.kind === 'voice' && (body.url
          ? <audio className="chats-audio" src={body.url} controls preload="none" onPointerDown={(e) => e.stopPropagation()} />
          : <VoiceNote duration={body.duration} />)}
        {body.kind === 'link' && (
          <>
            {/* Opens the PDF, video or page; in the home-screen app it opens over the app, which stays put. */}
            <a className="chats-linkcard" href={body.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}><b>{body.title}</b><span>{body.file ? `${body.file} · Tap to open` : body.url}</span></a>
            {body.text}
          </>
        )}
        {message.signOff && <div className="chats-msg-sign">{message.signOff}</div>}
        <span className="chats-msg-meta">
          {message.starred && <span className="chats-msg-star" aria-label="Starred"><StarIcon filled /></span>}
          {body.kind === 'voice' && body.duration && `${body.duration} · `}
          {message.edited && 'Edited · '}
          {message.at}
          {outgoing && <Ticks delivery={message.delivery} />}
        </span>
        {outgoing && message.delivery === 'failed' && (
          <div className="chats-msg-failed" role="status">Not delivered{message.failedReason ? `: ${message.failedReason}` : ''}</div>
        )}
        {message.button && <div className="chats-msg-button">{message.button}</div>}
        {message.buttons && message.buttons.length > 0 && (
          <div className="chats-msg-buttons">
            {message.buttons.map((b) => <div key={b} className="chats-msg-button">{b}</div>)}
          </div>
        )}
      </div>
      {all ? (
        <button
          type="button"
          className="chats-msg-reactions chats-msg-reactions--count"
          aria-label={`Reactions: ${all.map((r) => `${r.emoji} ${r.names.length}`).join(', ')}. Show who reacted`}
          title={all.map((r) => `${r.emoji} ${r.names.join(', ')}`).join('\n')}
          onClick={(e) => { e.stopPropagation(); onReactions?.(); }}
        >
          {all.map((r) => <span key={r.emoji}>{r.emoji}{r.names.length > 1 ? r.names.length : ''}</span>)}
        </button>
      ) : reactions.length > 0 && <span className="chats-msg-reactions" aria-label={`Reactions: ${reactions.join(' ')}`}>{reactions.join('')}</span>}
    </div>
  );
}

interface Props {
  items: ThreadItem[];
  /** The first `items` belong to the person's earlier chats: folded until "Show" is tapped. */
  earlier?: { items: number; chats: number; since: string };
  /** Open at this message, highlighted (from search or starred messages). */
  focusId?: string;
  typing?: boolean;
  /** Customer messages that were unread when the chat opened: a line marks where they start. */
  unreadAtOpen?: number;
  onReply?: (m: ChatMessage) => void;
  onMenu?: (m: ChatMessage) => void;
  /** A website enquiry's WhatsApp button: open the approved messages for this chat. */
  onLeadWhatsApp?: () => void;
  /** Platform Health, head office: mark a Sentinel post done or bring it back (Gary, 20 Oct). */
  onDone?: (m: ChatMessage, done: boolean) => void;
  /** Groups and channels: show who reacted to a message (22 Oct 2026). */
  onReactions?: (m: ChatMessage) => void;
}

export function Thread({ items: everything, earlier, focusId, typing = false, unreadAtOpen = 0, onReply, onMenu, onLeadWhatsApp, onDone, onReactions }: Props) {
  // Platform Health shows only posts still to deal with; done ones on "Show done" (Gary, 20 Oct). A date with
  // nothing left under it goes too.
  const [showDone, setShowDone] = useState(false);
  // Coaching and AI team posts fold in place when done instead (Gary, 3 Nov 2026: Done collapses the message).
  const doneCount = everything.filter((i) => i.from !== 'day' && i.from !== 'lead' && i.done && !i.coachPost && !i.aiPost).length;
  const all = showDone || !doneCount ? everything : everything
    .filter((i) => i.from === 'day' || i.from === 'lead' || !i.done || !!i.coachPost || !!i.aiPost)
    .filter((i, n, list) => i.from !== 'day' || (list[n + 1] !== undefined && list[n + 1]!.from !== 'day'));
  // A message to open at that sits in an earlier chat unfolds them.
  const [showEarlier, setShowEarlier] = useState(() => !!earlier && !!focusId && all.slice(0, earlier.items).some((i) => i.id === focusId));
  const [flash, setFlash] = useState<string | undefined>(focusId);
  const shown = earlier && !showEarlier ? all.slice(earlier.items) : all;
  // Ted's coaching posts fold to one line once a newer one arrives or it is marked done, with the replies under
  // it (Gary, 2 Nov 2026: silence the noise). Only the latest open post stays open; tap a line to open it.
  const [opened, setOpened] = useState<Set<string>>(() => new Set());
  const fold = foldCoachPosts(shown, opened, focusId);
  const items = fold.hidden.size ? shown.filter((i) => !fold.hidden.has(i.id)) : shown;
  const toggle = (id: string) => setOpened((o) => { const n = new Set(o); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const box = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const seen = useRef(all.length);
  const [newBelow, setNewBelow] = useState(0);
  const [showJump, setShowJump] = useState(false);

  // Where the "unread messages" line goes: before the first of the last N customer messages.
  const unreadLineBefore = (() => {
    if (!unreadAtOpen) return null;
    let left = unreadAtOpen;
    for (let i = items.length - 1; i >= 0; i -= 1) {
      const m = items[i];
      if (m && isMessage(m) && m.from === 'in') {
        left -= 1;
        if (left === 0) return m.id;
      }
    }
    return null;
  })();
  const firstRender = useRef(true);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    // Counted on the whole thread, so unfolding earlier chats is not "new messages".
    const added = all.length - seen.current;
    seen.current = all.length;
    const last = all[all.length - 1];
    const mine = !!last && isMessage(last) && last.from !== 'in';
    if (firstRender.current) {
      firstRender.current = false;
      const target = focusId ? el.querySelector(`[data-msg="${CSS.escape(focusId)}"]`) : null;
      const line = unreadLineBefore ? el.querySelector('[data-unread-line]') : null;
      // Centre it by scrolling the thread only (scrollIntoView can move the whole phone screen).
      const centre = (target ?? line) as HTMLElement | null;
      if (centre) el.scrollTop += centre.getBoundingClientRect().top - el.getBoundingClientRect().top - el.clientHeight / 2;
      else el.scrollTop = el.scrollHeight;
      return;
    }
    // Only a new message of yours brings the chat down; a tick or reaction on it does not.
    if (atBottom.current || (mine && added > 0)) {
      el.scrollTop = el.scrollHeight;
      setNewBelow(0);
    } else if (added > 0) {
      setNewBelow((n) => n + added);
    }
  }, [all, typing, unreadLineBefore]);

  // When the keyboard opens the chat gets shorter. If it was at the latest message, it stays
  // there, as WhatsApp does, instead of the last lines hiding behind the message box.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const keep = () => { if (atBottom.current) el.scrollTop = el.scrollHeight; };
    const watch = new ResizeObserver(keep);
    watch.observe(el);
    // A photo loading grows the chat too: stay on the latest message.
    el.addEventListener('load', keep, true);
    return () => { watch.disconnect(); el.removeEventListener('load', keep, true); };
  }, []);

  // The found message glows briefly, then settles.
  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(undefined), 2500);
    return () => window.clearTimeout(t);
  }, [flash]);

  function onScroll() {
    const el = box.current;
    if (!el) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    atBottom.current = near;
    setShowJump(!near);
    if (near) setNewBelow(0);
  }

  function jump() {
    const el = box.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  return (
    <div className="chats-thread-wrap">
      <div className="chats-thread" ref={box} onScroll={onScroll}>
        {doneCount > 0 && (
          <button type="button" className="chats-sys chats-earlier" onClick={() => setShowDone(!showDone)}>
            {showDone ? 'Hide done' : `Show done (${doneCount})`}
          </button>
        )}
        {!showDone && doneCount > 0 && !all.some((i) => i.from !== 'day' && i.from !== 'lead') && <p className="chats-empty">All done. Nothing needs action.</p>}
        {earlier && !showEarlier && (
          <button type="button" className="chats-sys chats-earlier" onClick={() => setShowEarlier(true)}>
            {earlier.chats === 1 ? `Earlier chat, ${earlier.since}` : `${earlier.chats} earlier chats, since ${earlier.since}`}. Tap to show
          </button>
        )}
        {items.map((item) => {
          if (item.from === 'day') return <div key={item.id} className="chats-day">{item.label}</div>;
          if (item.from === 'lead') return <LeadCard key={item.id} item={item} onWhatsApp={onLeadWhatsApp} />;
          if (isMessage(item) && item.coachPost && fold.folded.has(item.id)) {
            const replies = fold.replies.get(item.id) ?? 0;
            return (
              <button key={item.id} type="button" data-msg={item.id} className={`chats-coach-fold${item.done ? ' chats-coach-fold--done' : ''}`}
                onClick={() => toggle(item.id)} aria-label={`${item.coachPost.title}, ${item.coachPost.result}. Tap to open`}>
                <CoachHead post={item.coachPost} />
                <span className="chats-coach-result">{item.coachPost.result}</span>
                {replies > 0 && <span className="chats-coach-replies">{replies} repl{replies === 1 ? 'y' : 'ies'}</span>}
                {item.done && <span className="chats-coach-tick" aria-label="Done">✓</span>}
              </button>
            );
          }
          if (isMessage(item) && item.aiPost && item.done && !opened.has(item.id) && item.id !== focusId) {
            return (
              <button key={item.id} type="button" data-msg={item.id} className="chats-coach-fold chats-coach-fold--done"
                onClick={() => toggle(item.id)} aria-label={`${item.author ?? ''}: ${item.aiPost.headline}. Done. Tap to open`}>
                <span className="chats-coach-result">{item.author ? `${item.author}: ` : ''}{item.aiPost.headline}</span>
                <span className="chats-coach-tick" aria-label="Done">✓</span>
              </button>
            );
          }
          if (item.from === 'system') return <div key={item.id} className={`chats-sys${item.assistant ? ' chats-sys--leo' : ''}`}>{item.assistant && <AiTag />}{item.body.kind === 'text' ? item.body.text : ''}</div>;
          return (
            <div key={item.id} className={`chats-msg-row${item.id === flash ? ' chats-msg-row--found' : ''}`} data-msg={item.id}>
              {item.id === unreadLineBefore && (
                <div className="chats-unread-line" data-unread-line>{unreadAtOpen} unread message{unreadAtOpen > 1 ? 's' : ''}</div>
              )}
              <Swipeable
                className="chats-msg-swipe"
                selectable
                onLongPress={onMenu ? () => onMenu(item) : undefined}
                right={onReply ? { label: '', icon: <ReplyIcon />, run: () => onReply(item) } : undefined}
              >
                <Bubble message={item} onReactions={onReactions && item.reactionsAll ? () => onReactions(item) : undefined} />
              </Swipeable>
              {item.assistant && item.from !== 'in' && <DrcRate id={item.id} text={item.body.kind === 'text' ? item.body.text : ''} />}
              {onDone && item.doneable && (
                <div className="chats-train-judge">
                  {item.done ? (
                    <>
                      <small>Done by {item.done.by}, {item.done.at}</small>
                      <button type="button" className="chats-chip" onClick={() => onDone(item, false)}>Not done</button>
                    </>
                  ) : (
                    <button type="button" className="chats-chip" onClick={() => { onDone(item, true); if (item.coachPost || item.aiPost) setOpened((o) => { const n = new Set(o); n.delete(item.id); return n; }); }}>Done</button>
                  )}
                  {item.aiPost && item.done && (
                    <button type="button" className="chats-chip" onClick={() => toggle(item.id)}>Fold</button>
                  )}
                  {item.coachPost && fold.foldable.has(item.id) && (
                    <button type="button" className="chats-chip" onClick={() => toggle(item.id)}>Fold</button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {typing && <div className="chats-typing" aria-label="Typing"><i /><i /><i /></div>}
      </div>
      {showJump && (
        <button type="button" className="chats-jump" aria-label="Jump to latest message" onClick={jump}>
          <ArrowDownIcon />
          {newBelow > 0 && <span className="chats-badge chats-badge--sm chats-jump-count">{newBelow}</span>}
        </button>
      )}
    </div>
  );
}

/**
 * Which Ted coaching posts fold: all but the latest, and that one too once done, unless opened. folded: shown as one line;
 * hidden: the replies under a folded post (followed through reply chains); foldable: may fold (opened ones).
 * A message being opened at (search, starred) never hides.
 */
function foldCoachPosts(items: ThreadItem[], opened: Set<string>, focusId?: string) {
  const posts = items.filter((i): i is ChatMessage => isMessage(i) && !!i.coachPost);
  const last = posts[posts.length - 1];
  const latest = last && !last.done ? last : undefined;
  const foldable = new Set(posts.filter((p) => p !== latest).map((p) => p.id));
  const folded = new Set([...foldable].filter((id) => !opened.has(id)));
  const root = new Map<string, string>();
  const replies = new Map<string, number>();
  const hidden = new Set<string>();
  for (const i of items) {
    if (!isMessage(i) || !i.replyTo?.id) continue;
    const top = root.get(i.replyTo.id) ?? i.replyTo.id;
    root.set(i.id, top);
    if (!foldable.has(top)) continue;
    replies.set(top, (replies.get(top) ?? 0) + 1);
    if (folded.has(top) && i.id !== focusId) hidden.add(i.id);
  }
  if (focusId && folded.has(focusId)) folded.delete(focusId);
  return { folded, hidden, replies, foldable };
}
